import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, CheckCircle, Download } from "lucide-react";
import { useTeacherDashboard } from "../../hooks/useTeacherDashboard";
import type { Task, TaskResult } from "../../lib/taskEngine";
import { normalizeClassroomCode } from "../../lib/classroomEngine";
import { HOME_THEME as T } from "../../lib/homeTheme";
import { StaffDivider } from "../home/StaffDivider";

function pctColor(pct: number): string {
  if (pct >= 85) return T.mint;
  if (pct >= 60) return "#e9d5ff";
  return "#fca5a5";
}

function fmtWhen(ts?: number): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("es-CL", {
    day: "numeric",
    month: "short",
  });
}

function exportTaskCsv(task: Task, taskResults: TaskResult[]) {
  const headers = ["Estudiante", "Mejor Score", "Intentos", "Último intento"];
  const rows = taskResults.map((r) => [
    r.studentName || "Anónimo",
    `${(r.bestScore * 100).toFixed(0)}%`,
    String(r.attempts.length),
    fmtWhen(r.completedAt),
  ]);
  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `resultados_${task.id}_${task.title.replace(/\s+/g, "_")}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export const TeacherWorkshopPanel: React.FC<{
  classroomCode?: string;
  embedded?: boolean;
  hideTitle?: boolean;
  /** Sin StaffDivider superior (p. ej. en home con gap entre secciones) */
  noDivider?: boolean;
}> = ({ classroomCode, embedded = false, hideTitle = false, noDivider = false }) => {
  const d = useTeacherDashboard();
  const [expandedTask, setExpandedTask] = useState<string | null>(null);

  const scopedClassroom = classroomCode ? normalizeClassroomCode(classroomCode) : null;

  const matchesClassroom = (task: Task) => {
    if (!scopedClassroom) return true;
    return (
      Boolean(task.classroomId) &&
      normalizeClassroomCode(task.classroomId!) === scopedClassroom
    );
  };

  const scopedFilteredTasks = useMemo(
    () => d.filteredTasks.filter(matchesClassroom),
    [d.filteredTasks, scopedClassroom]
  );

  const scopedTasksWithoutAttempts = useMemo(
    () => d.tasksWithoutAttempts.filter(matchesClassroom),
    [d.tasksWithoutAttempts, scopedClassroom]
  );

  const scopedExpiringSoon = useMemo(
    () => d.expiringSoonTasks.filter(matchesClassroom),
    [d.expiringSoonTasks, scopedClassroom]
  );

  const scopedRecentSubmissions = useMemo(() => {
    if (!scopedClassroom) return d.recentSubmissions;
    const taskIds = new Set(
      d.tasks.filter(matchesClassroom).map((t) => t.id)
    );
    return d.recentSubmissions.filter((s) => taskIds.has(s.taskId));
  }, [d.recentSubmissions, d.tasks, scopedClassroom]);

  const alertItems = [
    ...(scopedTasksWithoutAttempts.length > 0
      ? [
          {
            label: `${scopedTasksWithoutAttempts.length} tarea${scopedTasksWithoutAttempts.length !== 1 ? "s" : ""} sin entregas`,
            meta: scopedTasksWithoutAttempts
              .slice(0, 3)
              .map((t) => t.id)
              .join(", "),
          },
        ]
      : []),
    ...(scopedExpiringSoon.length > 0
      ? [
          {
            label: `${scopedExpiringSoon.length} expiran esta semana`,
            meta: scopedExpiringSoon.map((t) => t.title).join(" · "),
          },
        ]
      : []),
  ];

  return (
    <section className={embedded || noDivider ? "" : "pt-10"}>
      {!embedded && !noDivider && <StaffDivider className="mb-10" />}

      <div
        className={`flex flex-col gap-4 text-left ${hideTitle ? "mb-6" : "mb-8 md:flex-row md:items-end md:justify-between"}`}
      >
        {!hideTitle && (
          <h2
            className="font-light tracking-tight text-left text-[clamp(1.5rem,3vw,2.25rem)] bg-clip-text text-transparent"
            style={{ backgroundImage: T.textGradient }}
          >
            {embedded ? "Tareas de la clase" : "Taller"}
          </h2>
        )}
        <div className="flex gap-6 text-[10px] tracking-[0.2em] uppercase">
          {(["active", "all", "expired"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => d.setFilterTab(tab)}
              className="transition-colors pb-1"
              style={{
                color: d.filterTab === tab ? T.text : T.dim,
                borderBottom: d.filterTab === tab ? `1px solid ${T.lavender}` : "1px solid transparent",
              }}
            >
              {tab === "all" ? "Todas" : tab === "active" ? "Activas" : "Expiradas"}
            </button>
          ))}
        </div>
      </div>

      {alertItems.length > 0 && (
        <ul className="mb-8 space-y-0">
          {alertItems.map((a) => (
            <li
              key={a.label}
              className="py-3 text-sm"
              style={{ color: T.muted, borderBottom: `1px solid ${T.lineItem}` }}
            >
              <span style={{ color: T.text }}>{a.label}</span>
              {a.meta && <span className="ml-3 opacity-70">{a.meta}</span>}
            </li>
          ))}
        </ul>
      )}

      {d.isLoading && (
        <p className="py-6 text-xs tracking-widest uppercase animate-pulse" style={{ color: T.dim }}>
          Cargando tareas…
        </p>
      )}

      {!d.isLoading && scopedFilteredTasks.length === 0 && (
        <p className="py-6 font-light text-base" style={{ color: T.muted }}>
          {embedded
            ? "Aún no hay tareas asignadas a esta clase. Créalas desde Escalas, Acordes o Canciones."
            : "Aún no has creado tareas. Usa «Crear tarea» en Escalas, Acordes o Canciones."}
        </p>
      )}

      <ul>
        {scopedFilteredTasks.map((task, i) => {
          const taskResults = d.results[task.id] || [];
          const isExpanded = expandedTask === task.id;

          return (
            <li key={task.id} style={{ borderBottom: `1px solid ${T.lineItem}` }}>
              <button
                type="button"
                onClick={() => {
                  const next = isExpanded ? null : task.id;
                  setExpandedTask(next);
                  if (next) d.handleExpand(task.id);
                }}
                className="group flex w-full items-baseline gap-4 py-4 text-left transition-all duration-300 hover:pl-2 hover:bg-white/[0.02]"
              >
                <span className="w-8 shrink-0 text-xs tabular-nums" style={{ color: T.dim }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className="flex-1 font-light text-lg tracking-tight group-hover:text-[#ddd6fe] transition-colors"
                  style={{ color: T.text }}
                >
                  {task.title}
                </span>
                <span className="text-xs tracking-wide shrink-0" style={{ color: T.muted }}>
                  {d.TYPE_LABELS[task.type]}
                </span>
                <span
                  className="text-lg font-extralight tabular-nums shrink-0 w-14 text-right"
                  style={{ color: pctColor(task.avgScorePct) }}
                >
                  {task.resultCount > 0 ? `${task.avgScorePct}%` : "—"}
                </span>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pl-10 pb-6"
                  >
                    <div
                      className="flex flex-wrap items-center gap-4 mb-4 text-xs tracking-wide"
                      style={{ color: T.muted }}
                    >
                      <span className="font-mono">{task.id}</span>
                      <button
                        type="button"
                        onClick={() => void d.handleCopyCode(task.id)}
                        className="inline-flex items-center gap-1 hover:text-white transition-colors"
                      >
                        {d.copiedCode === task.id ? (
                          <CheckCircle className="h-3.5 w-3.5" style={{ color: T.mint }} />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                        Copiar
                      </button>
                      {taskResults.length > 0 && (
                        <button
                          type="button"
                          onClick={() => exportTaskCsv(task, taskResults)}
                          className="inline-flex items-center gap-1 hover:text-white transition-colors"
                        >
                          <Download className="h-3.5 w-3.5" />
                          CSV
                        </button>
                      )}
                      <span>{fmtWhen(task.createdAt)}</span>
                      <span>
                        {task.resultCount} entrega{task.resultCount !== 1 ? "s" : ""}
                      </span>
                    </div>

                    {taskResults.length === 0 ? (
                      <p className="text-sm" style={{ color: T.dim }}>
                        Sin entregas todavía.
                      </p>
                    ) : (
                      <ul className="space-y-0">
                        {taskResults.map((res, idx) => (
                          <li
                            key={idx}
                            className="flex items-baseline justify-between gap-4 py-2"
                            style={{ borderTop: idx > 0 ? `1px solid ${T.lineItem}` : undefined }}
                          >
                            <span className="font-light" style={{ color: T.text }}>
                              {res.studentName || "Anónimo"}
                            </span>
                            <span className="text-sm tabular-nums" style={{ color: T.muted }}>
                              {res.attempts.length} intento{res.attempts.length !== 1 ? "s" : ""}
                            </span>
                            <span
                              className="text-lg font-extralight tabular-nums"
                              style={{ color: pctColor(res.bestScore * 100) }}
                            >
                              {(res.bestScore * 100).toFixed(0)}%
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          );
        })}
      </ul>

      {scopedRecentSubmissions.length > 0 && (
        <div className="mt-12 pt-6">
          {!embedded && <StaffDivider className="mb-8" />}
          <p className="mb-6 text-[10px] tracking-[0.22em] uppercase" style={{ color: T.muted }}>
            Entregas recientes
          </p>
          <ul>
            {scopedRecentSubmissions.slice(0, 6).map((sub, i) => (
              <li
                key={`${sub.taskId}-${sub.studentName}-${i}`}
                style={{ borderBottom: `1px solid ${T.lineItem}` }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setExpandedTask(sub.taskId);
                    d.handleExpand(sub.taskId);
                  }}
                  className="group flex w-full items-baseline gap-4 py-3 text-left hover:pl-1 transition-all"
                >
                  <span
                    className="flex-1 font-light group-hover:text-[#ddd6fe]"
                    style={{ color: T.text }}
                  >
                    {sub.studentName}
                  </span>
                  <span className="text-sm" style={{ color: T.muted }}>
                    {sub.taskTitle}
                  </span>
                  <span className="tabular-nums" style={{ color: pctColor(sub.bestScorePct) }}>
                    {sub.bestScorePct}%
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
